import qs from 'query-string'
import * as React from 'react'
import { Query } from 'react-apollo'
import { RouteComponentProps } from 'react-router'
import Alert from 'web-makeup/dist/Alert'
import Spin from 'web-makeup/dist/Spin'
import { capitalize, uniqBy, flatten, cloneDeep } from 'lodash'
import Component from '@reactions/component'

import {
  GetAllOrganisations,
  RequestTicket,
  RequestTicketRequesterType,
  RequestTicketState,
  RequestTicketUrgency,
  GetAllRequestTickets,
  OrderDirection,
} from '../../typings/types'
import TableList from '../TableList'
import { AllRequestTicketTypesQuery, GET_ALL_REQUEST_TICKET_TYPES } from '../RequestTypes'
import CheckboxPicker from '../CheckboxPicker'
import { isModerator } from '../../helpers/role-helper'
import ProducerFilter from '../ProducerRequests/ProducerFilter'
import { getStateName } from '../../helpers/state-helper'

import { ALL_ORGANISATIONS } from './queries/all-organisations'
import { StyledBox, Heading, Title, Filters } from './ProducerRequests.style'
import { GET_ALL_REQUEST_TICKETS } from './queries/all-requests'
import { requestsColumns } from './config/columns'

const ITEMS_PER_PAGE = 20
// George requested this request types to not be visible because they are currently not using it
const TEMPORARY_REMOVE_REQUEST_TYPES = ['quote', 'brand activation', 'action']

const onPageChange = (nextPage: number, history) => {
  const params = {
    ...qs.parse(location.search),
    page: nextPage,
  }
  history.push(`/requests?${qs.stringify(params)}`)
}

const onRowClick = (row: RequestTicket, history) => {
  history.push(`/requests/${row.uuid}`)
}

type FilterQueryStringsProps = Partial<{
  state: string
  requesterTypes: string
  assigneeEmail: string
  organisation: string
  requestType: string
  urgency: string
}>

export const handleFilterChange = (
  filterKey: keyof FilterQueryStringsProps,
  filterValue,
  props
) => {
  const { history, match, location } = props

  const prevFilters = qs.parse(location.search)
  const nextFilter: FilterQueryStringsProps = { [filterKey]: filterValue }

  const params = {
    ...prevFilters,
    page: undefined, // reset page count every time filter is applied
    ...nextFilter,
  }

  history.push(`${match.url}?${qs.stringify(params)}`)
}

type Params = Partial<{
  page: string
  pageSize: string
  state: string[]
  requesterTypes: string | string[]
  assigneeEmail: string[]
  requestType: string[]
  organisation: string[]
  urgency: string[]
}>

export const getQueryVariables = (params: Params): GetAllRequestTickets.Variables => ({
  page: (params.page && parseInt(params.page, 10)) || 1,
  pageSize: (params.pageSize && parseInt(params.pageSize, 10)) || ITEMS_PER_PAGE,
  orderDirection: OrderDirection.DESC,
  states: params.state as RequestTicketState[],
  requesterTypes: (params.requesterTypes as RequestTicketRequesterType[]) || [
    RequestTicketRequesterType.BRAND,
    RequestTicketRequesterType.CREATOR,
    RequestTicketRequesterType.PRODUCER,
  ],
  assigneeEmail: params.assigneeEmail,
  requestTypeName: params.requestType,
  billingOrganisationUuid: params.organisation,
  urgency: params.urgency as RequestTicketUrgency[],
})

const ProducerRequests: React.SFC<RouteComponentProps> = props => {
  const params: Params = qs.parse(location.search)
  const allStates = Object.keys(RequestTicketState)

  if (!isModerator()) allStates.splice(6, 2)

  return (
    <Query<GetAllRequestTickets.Query, GetAllRequestTickets.Variables>
      query={GET_ALL_REQUEST_TICKETS}
      variables={getQueryVariables(params)}
      pollInterval={10000}
      fetchPolicy="network-only"
    >
      {({ loading, error, data }) => {
        if (error) {
          return error.graphQLErrors.map((e, i) => (
            <Alert message="Error" description={e.message} key={i} type="error" />
          ))
        }

        const tableData =
          (data && data.allRequestTickets && data.allRequestTickets.requestTickets) || []

        tableData.forEach(ticket => {
          if (ticket.target && 'project' in ticket.target) {
            ticket.target = ticket.target.project
          }
        })

        return (
          <Component initialState={{ columns: requestsColumns }}>
            {({ state, setState }) => (
              <StyledBox>
                <Heading>
                  <Title>Requests</Title>
                  <Filters>
                    <li data-qa="column-picker">
                      <CheckboxPicker
                        defaultValue={requestsColumns
                          .filter(col => !!col.name && col.visible)
                          .map(col => col.name)}
                        options={requestsColumns.filter(col => !!col.name).map(col => col.name)}
                        onChange={values => {
                          const columnsArr = cloneDeep(state.columns)
                          columnsArr.filter(col => !!col.name).forEach(column => {
                            column.visible = values.indexOf(column.name) !== -1
                          })
                          setState({ ...state, columns: columnsArr })
                        }}
                        placeholder="Columns"
                      />
                    </li>
                    <li>
                      <CheckboxPicker
                        defaultValue={flatten([params.urgency!])}
                        options={Object.keys(RequestTicketUrgency).map(value => {
                          const label = capitalize(RequestTicketUrgency[value].replace('_', ' '))
                          return { label, value }
                        })}
                        onChange={values => handleFilterChange('urgency', values, props)}
                        placeholder="Urgency"
                      />
                    </li>
                    <li>
                      <Query<GetAllOrganisations.Query> query={ALL_ORGANISATIONS}>
                        {({ loading: organisationsLoading, data: organisationsData }) => (
                          <Spin spinning={organisationsLoading}>
                            {organisationsData &&
                              organisationsData.allBillingOrganisations && (
                                <CheckboxPicker
                                  defaultValue={flatten([params.organisation!])}
                                  options={organisationsData.allBillingOrganisations.map(
                                    organisation => {
                                      const value = organisation.uuid
                                      const label = organisation.name.replace('90 Seconds', '')
                                      return { label, value }
                                    }
                                  )}
                                  onChange={values =>
                                    handleFilterChange('organisation', values, props)
                                  }
                                  placeholder="Organisation"
                                />
                              )}
                          </Spin>
                        )}
                      </Query>
                    </li>
                    <li>
                      <AllRequestTicketTypesQuery query={GET_ALL_REQUEST_TICKET_TYPES}>
                        {({ data: requestTypes }) => {
                          const categories = requestTypes
                            ? uniqBy(requestTypes.allRequestTicketTypes, 'requestType')
                            : []
                          return (
                            <CheckboxPicker
                              defaultValue={flatten([params.requestType!])}
                              options={categories
                                .filter(
                                  x => !TEMPORARY_REMOVE_REQUEST_TYPES.includes(x.requestType)
                                )
                                .map(key => {
                                  const value = key.requestType
                                  const label = capitalize(value).replace('_', ' ')
                                  return { label, value }
                                })}
                              onChange={values => handleFilterChange('requestType', values, props)}
                              placeholder="Request Type"
                            />
                          )
                        }}
                      </AllRequestTicketTypesQuery>
                    </li>
                    <li>
                      <ProducerFilter
                        assigneeEmails={params.assigneeEmail}
                        onChange={values => handleFilterChange('assigneeEmail', values, props)}
                      />
                    </li>
                    <li>
                      <CheckboxPicker
                        defaultValue={flatten([params.requesterTypes!])}
                        options={Object.keys(RequestTicketRequesterType).map(value => {
                          const label = capitalize(
                            RequestTicketRequesterType[value].replace('GEN1_', '')
                          )
                          return { label, value }
                        })}
                        onChange={values => handleFilterChange('requesterTypes', values, props)}
                        placeholder="Requester Type"
                      />
                    </li>
                    <li>
                      <CheckboxPicker
                        defaultValue={flatten([params.state!])}
                        options={allStates.map(value => {
                          const label = getStateName(RequestTicketState[value])
                          return { label, value }
                        })}
                        onChange={values => handleFilterChange('state', values, props)}
                        placeholder="State"
                      />
                    </li>
                  </Filters>
                </Heading>
                <TableList
                  data={tableData}
                  columns={state.columns}
                  onPageChange={nextPage => onPageChange(nextPage, props.history)}
                  onRowClick={row => onRowClick(row, props.history)}
                  loading={loading}
                  pagination={{
                    pageSize: getQueryVariables(params).pageSize || ITEMS_PER_PAGE,
                    current:
                      data && data.allRequestTickets && data.allRequestTickets.pageInfo.currentPage,
                    total:
                      data && data.allRequestTickets && data.allRequestTickets.pageInfo.totalCount,
                  }}
                />
              </StyledBox>
            )}
          </Component>
        )
      }}
    </Query>
  )
}

export default ProducerRequests



// WEBPACK FOOTER //
// ./src/components/ProducerRequests/ProducerRequests.tsx